import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ScanLine, Users, DoorOpen, Bus, Luggage, Radio, Download, X } from 'lucide-react';
import { get, del, download } from '../api/client';
import { useToast } from '../context/ToastContext';
import { Card, Stat, Badge, Modal, Button, Select, Avatar, Loader, EmptyState } from './UI';
import { fmtTime, fmtRelative, fmtNumber } from '../utils/format';

/**
 * Scans as they happen.
 *
 * `ScanStats` are the tiles on the event dashboard; clicking one opens
 * `LiveScanFeed` on that kind of scan. Both poll the same endpoint, which
 * returns only what has arrived since the last answer.
 */
export const SCAN_KINDS = [
  { key: 'event_entry', label: 'Event entry', short: 'Entry scans', icon: DoorOpen, tone: 'success' },
  { key: 'session', label: 'Session attendance', short: 'Session scans', icon: ScanLine, tone: 'gold' },
  { key: 'bus_boarding', label: 'Coach boarding', short: 'Boardings', icon: Bus, tone: 'info' },
  { key: 'luggage', label: 'Luggage', short: 'Luggage scans', icon: Luggage, tone: 'warning' },
  { key: 'hotel_checkin', label: 'Hotel check-in', short: 'Hotel check-ins', icon: Users, tone: 'neutral' },
];

/** Polls /attendance/live, keeping the newest rows at the top. */
function useLiveScans(eventId, { scanType, sessionId, active = true, every = 6000 } = {}) {
  const [state, setState] = useState({ feed: [], byType: {}, totals: null, loading: true });
  const newest = useRef(null);
  const seen = useRef(new Set());

  const poll = useCallback(async () => {
    if (!eventId) return;
    try {
      const d = await get('/attendance/live', {
        event: eventId,
        ...(scanType ? { scanType } : {}),
        ...(sessionId ? { sessionId } : {}),
        ...(newest.current ? { since: newest.current } : {}),
        limit: 100,
      });
      setState((prev) => {
        const fresh = (d.feed || []).filter((r) => !seen.current.has(r._id));
        fresh.forEach((r) => seen.current.add(r._id));
        const feed = [...fresh, ...prev.feed].slice(0, 300);
        if (feed[0]) newest.current = feed[0].scannedAt;
        return { feed, byType: d.byType || {}, totals: d.totals || null, loading: false, at: d.at };
      });
    } catch {
      setState((prev) => ({ ...prev, loading: false }));   // a dropped poll is picked up by the next one
    }
  }, [eventId, scanType, sessionId]);

  useEffect(() => {
    // A changed filter starts the list again.
    newest.current = null; seen.current = new Set();
    setState({ feed: [], byType: {}, totals: null, loading: true });
  }, [eventId, scanType, sessionId]);

  useEffect(() => {
    if (!active || !eventId) return undefined;
    poll();
    const t = setInterval(poll, every);
    return () => clearInterval(t);
  }, [active, eventId, every, poll]);

  return state;
}

/** The dashboard tiles. Clicking one opens the live list for that kind. */
export function ScanStats({ eventId, hasModule, readOnly = false }) {
  const { byType, totals } = useLiveScans(eventId, { every: 15000 });
  const [openKind, setOpenKind] = useState(null);

  const kinds = SCAN_KINDS.filter((k) => {
    if (k.key === 'bus_boarding') return hasModule ? hasModule('buses') : true;
    if (k.key === 'luggage') return hasModule ? hasModule('onboardingLuggage') || hasModule('arrivalLuggage') : true;
    if (k.key === 'hotel_checkin') return (byType.hotel_checkin?.scans || 0) > 0;
    return true;
  });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '4px 0 11px' }}>
        <Radio size={14} color="var(--gold-500)" />
        <p style={{ fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.9, color: 'var(--text-lo)' }}>
          Scans — live
        </p>
        <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>
          {totals ? `${fmtNumber(totals.today)} today · ${fmtNumber(totals.lastHour)} in the last hour` : 'counting…'}
        </span>
        {totals?.companions > 0 && (
          <span style={{ fontSize: 12, color: 'var(--gold-300)' }}>
            {fmtNumber(totals.people)} people in, {fmtNumber(totals.companions)} of them brought along
          </span>
        )}
        <button type="button" onClick={() => setOpenKind('all')} style={{ marginLeft: 'auto', fontSize: 12.5, color: 'var(--gold-300)' }}>
          See every scan
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 13, marginBottom: 20 }}>
        {kinds.map((k) => (
          <button key={k.key} type="button" onClick={() => setOpenKind(k.key)} style={{ textAlign: 'left', display: 'block' }}
            title={`Show the ${k.label.toLowerCase()} scans as they happen`}>
            <Stat
              label={k.short}
              value={fmtNumber(byType[k.key]?.scans || 0)}
              sub={`${fmtNumber(byType[k.key]?.uniquePeople || 0)} people · tap for the list`}
              icon={k.icon}
              tone={k.tone}
            />
          </button>
        ))}
      </div>

      {openKind && (
        <LiveScanFeed
          eventId={eventId}
          initialKind={openKind === 'all' ? '' : openKind}
          readOnly={readOnly}
          onClose={() => setOpenKind(null)}
        />
      )}
    </>
  );
}

/** The list itself: newest first, arriving on its own. */
export function LiveScanFeed({ eventId, initialKind = '', sessionId, onClose, title, readOnly = false }) {
  const toast = useToast();
  const [kind, setKind] = useState(initialKind);
  const { feed, byType, totals, loading } = useLiveScans(eventId, { scanType: kind || undefined, sessionId });
  // Rows taken off by hand, hidden straight away rather than waiting for a poll.
  const [dropped, setDropped] = useState([]);

  const drop = async (row) => {
    try {
      const res = await del(`/attendance/${row._id}`);
      setDropped((d) => [...d, row._id]);
      toast.success(res.message);
    } catch (err) { toast.error(err.message); }
  };

  const save = async () => {
    try { await download('/attendance/export', { event: eventId, ...(kind ? { scanType: kind } : {}) }, 'scans.xlsx'); }
    catch (err) { toast.error(err.message); }
  };

  const shown = SCAN_KINDS.find((k) => k.key === kind);

  return (
    <Modal
      open onClose={onClose} width={640}
      title={title || (shown ? shown.label : 'Every scan')}
      subtitle={totals ? `${fmtNumber(totals.scans)} in total · ${fmtNumber(totals.today)} today · updating by itself` : 'Loading…'}
      footer={<><Button variant="subtle" icon={Download} onClick={save}>Excel</Button><Button onClick={onClose}>Close</Button></>}
    >
      {!sessionId && (
        <Select value={kind} onChange={(e) => setKind(e.target.value)} style={{ marginBottom: 12 }}>
          <option value="">Every kind of scan</option>
          {SCAN_KINDS.map((k) => (
            <option key={k.key} value={k.key}>{k.label}{byType[k.key] ? ` (${byType[k.key].scans})` : ''}</option>
          ))}
        </Select>
      )}

      {loading && !feed.length ? <Loader label="Loading scans" /> : null}
      {!loading && !feed.length ? (
        <EmptyState message="No scans yet" hint="Entries appear here the moment somebody is scanned." icon={ScanLine} />
      ) : null}

      <div style={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <AnimatePresence initial={false}>
          {feed.filter((r) => !dropped.includes(r._id)).map((r) => {
            const k = SCAN_KINDS.find((x) => x.key === r.scanType);
            const Icon = k?.icon || ScanLine;
            return (
              <motion.div key={r._id} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 10px', borderRadius: 'var(--r-sm)', borderBottom: '1px solid var(--border)' }}>
                <Avatar name={r.user?.name || '?'} size={32} vip={r.user?.isVip} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13.5, fontWeight: 500 }}>{r.user?.name || 'Unknown'}</p>
                  <p style={{ fontSize: 11.5, color: 'var(--text-lo)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {[r.sessionTitle, r.gate, r.location, r.selfScanned ? 'scanned it themselves' : (r.scannedBy?.name ? `by ${r.scannedBy.name}` : 'scanned at the desk')].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <Badge tone={k?.tone || 'neutral'}><Icon size={10} /> {k?.short || r.scanType}</Badge>
                  {r.companions > 0 && (
                    <Badge tone={r.partyState === 'up' ? 'warning' : 'success'} style={{ marginLeft: 5 }}>+{r.companions}</Badge>
                  )}
                  <p style={{ fontSize: 11, color: 'var(--text-lo)', marginTop: 3 }} title={fmtTime(r.scannedAt)}>{fmtRelative(r.scannedAt)}</p>
                </div>
                {!readOnly && (
                  <button type="button" onClick={() => drop(r)} title="Take this scan off the list"
                    style={{ color: 'var(--text-lo)', padding: 4, display: 'flex', flexShrink: 0 }}>
                    <X size={14} />
                  </button>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </Modal>
  );
}

/** A compact always-on panel for a dashboard column. */
export function LiveScanPanel({ eventId, limit = 8 }) {
  const { feed, totals } = useLiveScans(eventId);
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
        <Radio size={15} color="var(--gold-500)" />
        <p style={{ fontSize: 14.5, fontWeight: 600 }}>Live scans</p>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-lo)' }}>{totals ? `${fmtNumber(totals.today)} today` : ''}</span>
      </div>
      {!feed.length ? (
        <p style={{ fontSize: 13, color: 'var(--text-lo)' }}>Nothing scanned in the last two hours.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {feed.slice(0, limit).map((r) => (
            <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar name={r.user?.name || '?'} size={28} vip={r.user?.isVip} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 13 }}>{r.user?.name || 'Unknown'}</p>
                <p style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{r.sessionTitle || SCAN_KINDS.find((k) => k.key === r.scanType)?.label || r.scanType}</p>
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>{fmtTime(r.scannedAt)}</span>
            </div>
          ))}
        </div>
      )}
      <Button variant="subtle" size="sm" onClick={() => setOpen(true)} style={{ marginTop: 12 }}>Open the full list</Button>
      {open && <LiveScanFeed eventId={eventId} onClose={() => setOpen(false)} />}
    </Card>
  );
}
